import { safeHref } from './agent-ia.models';

// A small, bounded Markdown reader for assistant messages. It returns text nodes, never HTML.
// Rendering uses Angular interpolation; raw HTML and images stay inert and links are allowlisted.
export interface MarkdownInline {
  kind: 'text' | 'strong' | 'em' | 'strike' | 'code' | 'link';
  text?: string; href?: string; children?: MarkdownInline[];
}
export interface MarkdownItem { blocks: MarkdownBlock[]; checked?: boolean; }
export interface MarkdownBlock {
  kind: 'paragraph' | 'heading' | 'code' | 'quote' | 'list' | 'table' | 'rule';
  inline?: MarkdownInline[]; level?: number; text?: string; language?: string;
  blocks?: MarkdownBlock[]; ordered?: boolean; start?: number; items?: MarkdownItem[];
  header?: MarkdownInline[][]; rows?: MarkdownInline[][][]; align?: ('left' | 'center' | 'right')[];
}
const MAX_DEPTH = 8;
const textNode = (text: string): MarkdownInline => ({ kind: 'text', text });

export function markdownInline(source: string, depth = 0): MarkdownInline[] {
  if (depth >= MAX_DEPTH) return [textNode(source)];
  const nodes: MarkdownInline[] = [];
  let plain = '', i = 0;
  const flush = () => { if (plain) { nodes.push(textNode(plain)); plain = ''; } };
  while (i < source.length) {
    if (source[i] === '\\' && /[\\`*_{}[\]()#+.!>|~-]/.test(source[i + 1] || '')) {
      plain += source[i + 1]; i += 2; continue;
    }
    if (source[i] === '`') {
      const ticks = source.slice(i).match(/^`+/)![0];
      const end = source.indexOf(ticks, i + ticks.length);
      if (end >= 0) {
        flush(); nodes.push({ kind: 'code', text: source.slice(i + ticks.length, end).replace(/\n/g, ' ') });
        i = end + ticks.length; continue;
      }
    }
    const image = source.startsWith('![', i), linkStart = image ? i + 1 : i;
    if (source[linkStart] === '[') {
      const labelEnd = source.indexOf('](', linkStart + 1);
      if (labelEnd >= 0 && labelEnd - linkStart <= 500) {
        let end = labelEnd + 2, balance = 1;
        for (; end < source.length && end - labelEnd < 2000; end++) {
          if (source[end] === '\\') { end++; continue; }
          if (source[end] === '(') balance++;
          if (source[end] === ')' && --balance === 0) break;
        }
        if (balance === 0) {
          const label = source.slice(linkStart + 1, labelEnd);
          const target = source.slice(labelEnd + 2, end).trim().replace(/\s+["'][^\n]*["']$/, '').replace(/^<(.+)>$/, '$1');
          flush();
          if (!image && safeHref(target)) nodes.push({ kind: 'link', href: target, children: markdownInline(label, depth + 1) });
          else nodes.push(textNode(label));
          i = end + 1; continue;
        }
      }
    }
    if (source.startsWith('https://', i) || source.startsWith('http://', i) || source.startsWith('<http', i)) {
      const angle = source[i] === '<';
      const raw = source.slice(i + (angle ? 1 : 0)).match(/^https?:\/\/[^\s<>]+/)?.[0];
      const url = angle ? raw : raw?.replace(/[.,;!?:)]+$/, '');
      if (url && safeHref(url) && (!angle || source[i + url.length + 1] === '>')) {
        flush(); nodes.push({ kind: 'link', href: url, children: [textNode(url)] });
        i += url.length + (angle ? 2 : 0); continue;
      }
    }
    const marker = ['***', '___', '**', '__', '~~', '*', '_'].find(value => source.startsWith(value, i));
    if (marker && !(marker[0] === '_' && /[\p{L}\p{N}]/u.test(source[i - 1] || ''))) {
      const end = source.indexOf(marker, i + marker.length);
      if (end > i + marker.length && !/\s/.test(source[i + marker.length]) && !/\s/.test(source[end - 1])) {
        flush();
        const children = markdownInline(source.slice(i + marker.length, end), depth + 1);
        nodes.push({ kind: marker === '~~' ? 'strike' : marker.length === 1 ? 'em' : 'strong',
          children: marker.length === 3 ? [{ kind: 'em', children }] : children });
        i = end + marker.length; continue;
      }
    }
    plain += source[i++];
  }
  flush();
  return nodes;
}

const markerOf = (line: string) => line.match(/^( {0,3})([-+*]|\d+[.)])\s+(.*)$/);
const fenceOf = (line: string) => line.match(/^ {0,3}(`{3,}|~{3,})(.*)$/);
const headingOf = (line: string) => line.match(/^ {0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
const ruleOf = (line: string) => /^ {0,3}(?:\*\s*){3,}$|^ {0,3}(?:-\s*){3,}$|^ {0,3}(?:_\s*){3,}$/.test(line);

function cellsOf(line: string): string[] {
  const source = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let cell = '', code = false;
  for (let i = 0; i < source.length; i++) {
    if (source[i] === '\\' && source[i + 1] === '|') { cell += '|'; i++; continue; }
    if (source[i] === '`') code = !code;
    if (source[i] === '|' && !code) { cells.push(cell.trim()); cell = ''; } else cell += source[i];
  }
  cells.push(cell.trim());
  return cells;
}
const tableSeparator = (line: string) => line.includes('-') && cellsOf(line).every(cell => /^:?-{3,}:?$/.test(cell));
const blockStart = (line: string) => Boolean(fenceOf(line) || headingOf(line) || ruleOf(line) || markerOf(line) || /^ {0,3}>/.test(line));

function blocksOf(lines: string[], depth: number): MarkdownBlock[] {
  if (depth >= MAX_DEPTH) return [{ kind: 'paragraph', inline: [textNode(lines.join('\n'))] }];
  const blocks: MarkdownBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const fence = fenceOf(line);
    if (fence) {
      const code: string[] = [];
      i++;
      while (i < lines.length) {
        const closing = fenceOf(lines[i]);
        if (closing && closing[1][0] === fence[1][0] && closing[1].length >= fence[1].length && !closing[2].trim()) { i++; break; }
        code.push(lines[i++]);
      }
      blocks.push({ kind: 'code', text: code.join('\n'), language: fence[2].trim().slice(0, 30) }); continue;
    }
    const heading = headingOf(line);
    if (heading) { blocks.push({ kind: 'heading', level: heading[1].length, inline: markdownInline(heading[2]) }); i++; continue; }
    if (ruleOf(line)) { blocks.push({ kind: 'rule' }); i++; continue; }
    if (/^ {0,3}>/.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && /^ {0,3}>/.test(lines[i])) quoted.push(lines[i++].replace(/^ {0,3}> ?/, ''));
      blocks.push({ kind: 'quote', blocks: blocksOf(quoted, depth + 1) }); continue;
    }
    if (i + 1 < lines.length && line.includes('|') && tableSeparator(lines[i + 1])) {
      const header = cellsOf(line), separators = cellsOf(lines[i + 1]);
      if (header.length === separators.length) {
        const rows: MarkdownInline[][][] = [];
        i += 2;
        while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
          const cells = cellsOf(lines[i++]);
          rows.push(header.map((_, n) => markdownInline(cells[n] || '')));
        }
        blocks.push({ kind: 'table', header: header.map(cell => markdownInline(cell)), rows,
          align: separators.map(cell => cell.startsWith(':') && cell.endsWith(':') ? 'center' : cell.endsWith(':') ? 'right' : 'left') });
        continue;
      }
    }
    const firstMarker = markerOf(line);
    if (firstMarker) {
      const ordered = /^\d/.test(firstMarker[2]);
      const items: MarkdownItem[] = [];
      while (i < lines.length) {
        const marker = markerOf(lines[i]);
        if (!marker || marker[1].length !== firstMarker[1].length || /^\d/.test(marker[2]) !== ordered) break;
        const task = marker[3].match(/^\[([ xX])\]\s+(.*)$/);
        const itemLines = [task ? task[2] : marker[3]];
        const contentIndent = lines[i].length - marker[3].length;
        i++;
        while (i < lines.length) {
          const next = markerOf(lines[i]);
          if (next && next[1].length <= firstMarker[1].length) break;
          if (!lines[i].trim()) {
            const following = lines[i + 1] || '';
            if (!following.startsWith(' '.repeat(firstMarker[1].length + 2)) && !markerOf(following)) break;
            itemLines.push(''); i++; continue;
          }
          const indent = lines[i].match(/^ */)![0].length;
          if (indent <= firstMarker[1].length) break;
          itemLines.push(lines[i++].slice(Math.min(indent, contentIndent)));
        }
        items.push({ blocks: blocksOf(itemLines, depth + 1), ...(task ? { checked: task[1].toLowerCase() === 'x' } : {}) });
      }
      blocks.push({ kind: 'list', ordered, start: ordered ? Math.min(parseInt(firstMarker[2], 10), 1000000) : undefined, items }); continue;
    }
    if (i + 1 < lines.length && /^ {0,3}(?:=+|-+)\s*$/.test(lines[i + 1])) {
      blocks.push({ kind: 'heading', level: lines[i + 1].trim()[0] === '=' ? 1 : 2, inline: markdownInline(line) }); i += 2; continue;
    }
    const paragraph = [line]; i++;
    while (i < lines.length && lines[i].trim() && !blockStart(lines[i])) {
      if (i + 1 < lines.length && lines[i].includes('|') && tableSeparator(lines[i + 1])) break;
      paragraph.push(lines[i++]);
    }
    blocks.push({ kind: 'paragraph', inline: markdownInline(paragraph.join('\n').replace(/ {2,}\n/g, '\n')) });
  }
  return blocks;
}

export function markdownBlocks(content: string): MarkdownBlock[] {
  return blocksOf(String(content || '').slice(0, 20000).replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n'), 0);
}
